import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import * as React from 'react';
import { ClickAwayListener, InputLabel, Popper, Fade } from '@mui/material';
import { Button } from '../Button/Button.js';
import { IconButton } from '../IconButton/IconButton.js';
import { LayoutGrid } from '../LayoutGrid/LayoutGrid.js';
import { List } from '../List/List.js';
import { ListItem } from '../ListItem/ListItem.js';
import { Paper } from '../Paper/Paper.js';
import { Typography } from '../Typography/Typography.js';
import '../../utils/themeProvider.js';
import '@mui/styles';
import '@mui/material/styles';
import keygen from 'uuid/v4';
/* Please note:  this component currently is built using an outdated approach.
If you want to pattern a new component after previous work, the use the ComboButton */

var styles = {
  menuListWrapper: {
    padding: 0,
    boxShadow: 2
  },
  triggerButton: {
    padding: 1
  },
  listRoot: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0
  },
  listItem: {
    justifyContent: 'flex-start',
    width: 'auto',
    padding: 1
  },
  triggerPadding: {
    '&>p ': {
      paddingRight: 0.5
    },
    '&>svg': {
      paddingLeft: 0.5
    }
  },
  popperContainer: {
    zIndex: 'modal'
  }
};

var findFirstFocusable = function findFirstFocusable(childNodes) {
  var firstFocusIndex = 0;
  var i = 0;

  while (i < childNodes.length) {
    if (childNodes[i].getAttribute('tabindex') === '0') {
      firstFocusIndex = i;
      break;
    } else {
      i += 1;
    }
  }

  return firstFocusIndex;
};

var findLastFocusable = function findLastFocusable(childNodes) {
  var lastFocusIndex = 0;
  var i = childNodes.length - 1;

  while (i >= 0) {
    if (childNodes[i].getAttribute('tabindex') === '0') {
      lastFocusIndex = i;
      break;
    } else {
      i -= 1;
    }
  }

  return lastFocusIndex;
};

var keyListeners = {
  tab: 9,
  esc: 27,
  etr: 13,
  spc: 32
};

var keyDownOpenClose = function keyDownOpenClose(e, atEnd, atBeginning, buttonRef) {
  var tab = keyListeners.tab,
      esc = keyListeners.esc;
  var keyCode = e.keyCode,
      shiftKey = e.shiftKey;
  var isOpen = true;

  if (shiftKey && keyCode === tab && atBeginning) {
    isOpen = false;
  }

  if (!shiftKey && keyCode === tab && atEnd) {
    isOpen = false;
  }

  if (keyCode === esc) {
    isOpen = false;
  }

  if (!isOpen && buttonRef && buttonRef.current) {
    buttonRef.current.focus();
  }

  return isOpen;
};

var ContextualMenuPopperBase = function ContextualMenuPopperBase(_ref) {
  var _ref$trigger = _ref.trigger,
      text = _ref$trigger.text,
      showText = _ref$trigger.showText,
      icon = _ref$trigger.icon,
      triggerSelector = _ref$trigger.triggerSelector,
      _ref$trigger$popperPo = _ref$trigger.popperPosition,
      popperPosition = _ref$trigger$popperPo === void 0 ? 'bottom-end' : _ref$trigger$popperPo,
      menuItems = _ref.menuItems,
      translate = _ref.translate,
      sxButton = _ref.sxButton,
      sxPopper = _ref.sxPopper;
  var listRoot = styles.listRoot,
      listItem = styles.listItem,
      menuListWrapper = styles.menuListWrapper,
      popperContainer = styles.popperContainer;

  var _React$useState = React.useState(null),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      anchorEl = _React$useState2[0],
      setAnchorEl = _React$useState2[1];

  var _React$useState3 = React.useState(false),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      open = _React$useState4[0],
      setOpen = _React$useState4[1];

  var _React$useState5 = React.useState(false),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      fromKeyboard = _React$useState6[0],
      setFromKeyBoard = _React$useState6[1];

  var _React$useState7 = React.useState(0),
      _React$useState8 = _slicedToArray(_React$useState7, 2),
      keyPressed = _React$useState8[0],
      setKeyPressed = _React$useState8[1];

  var _React$useState9 = React.useState(),
      _React$useState10 = _slicedToArray(_React$useState9, 2),
      placement = _React$useState10[0],
      setPlacement = _React$useState10[1];

  var _React$useState11 = React.useState(null),
      _React$useState12 = _slicedToArray(_React$useState11, 2),
      childNodes = _React$useState12[0],
      setChildNodes = _React$useState12[1];

  var _React$useState13 = React.useState(),
      _React$useState14 = _slicedToArray(_React$useState13, 2),
      node = _React$useState14[0],
      setNode = _React$useState14[1];

  var _React$useState15 = React.useState(0),
      _React$useState16 = _slicedToArray(_React$useState15, 2),
      firstFocusable = _React$useState16[0],
      setFirstFocusable = _React$useState16[1];

  var _React$useState17 = React.useState(1),
      _React$useState18 = _slicedToArray(_React$useState17, 2),
      lastFocusable = _React$useState18[0],
      setLastFocusable = _React$useState18[1];

  var defaultText = translate('cmmn:open');
  var buttonRef = React.useRef(null);
  var buttonId = keygen();

  var handleClick = function handleClick(newPlacement) {
    return function (event) {
      if (keyPressed === keyListeners.etr) {
        return;
      }

      setFromKeyBoard(false);
      setElmPosition(newPlacement, event);
    };
  };

  var setElmPosition = function setElmPosition(newPlacement, event) {
    setAnchorEl(event.currentTarget);
    setOpen(function (prev) {
      return placement !== newPlacement || !prev;
    });
    setPlacement(newPlacement);
  };

  var handleBtnKeyDown = function handleBtnKeyDown(newPlacement) {
    return function (event) {
      var etr = keyListeners.etr,
          spc = keyListeners.spc;
      var keyCode = event.keyCode;
      setKeyPressed(keyCode);
      var displayMenu = keyCode === etr || keyCode === spc;
      setFromKeyBoard(displayMenu);
      displayMenu && setElmPosition(newPlacement, event);
    };
  };

  var handleClickAway = function handleClickAway() {
    return setOpen(false);
  };

  var handleKeyDown = function handleKeyDown(e, index) {
    var atEnd = index === lastFocusable;
    var atBeginning = index === firstFocusable;
    setOpen(keyDownOpenClose(e, atEnd, atBeginning, buttonRef));
  };

  var handleBlur = function handleBlur(e) {
    e.preventDefault();
  };

  var menuListRef = React.useCallback(function (value) {
    setNode(value);
  }, []);
  React.useEffect(function () {
    if (node) {
      setChildNodes(node.children);
    }
  }, [node]);
  React.useEffect(function () {
    if (childNodes) {
      setFirstFocusable(findFirstFocusable(childNodes));
      setLastFocusable(findLastFocusable(childNodes));
      fromKeyboard && childNodes[firstFocusable].focus();
    }
  }, [childNodes, fromKeyboard]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(LayoutGrid, {
    container: true,
    direction: "row",
    justify: "center",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(ClickAwayListener, {
    onClickAway: React.useCallback(handleClickAway, [])
  }, icon && !showText ? /*#__PURE__*/React.createElement(IconButton, {
    icon: icon,
    label: text || defaultText,
    title: text || defaultText,
    onClick: handleClick(popperPosition),
    onKeyDown: handleBtnKeyDown(popperPosition),
    dataProp: triggerSelector,
    id: buttonId,
    ref: buttonRef,
    sx: sxButton
  }) : /*#__PURE__*/React.createElement(Button, {
    onClick: handleClick(popperPosition),
    onKeyDown: handleBtnKeyDown(popperPosition),
    "aria-label": text || defaultText,
    label: text || defaultText,
    icon: icon,
    dataProp: triggerSelector,
    id: buttonId,
    ref: buttonRef,
    title: text || defaultText,
    sx: sxButton
  }, text || defaultText)), /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: buttonId,
    "aria-label": text || defaultText
  })), /*#__PURE__*/React.createElement(Popper, {
    open: open,
    anchorEl: anchorEl,
    placement: placement,
    transition: true,
    disablePortal: true,
    role: "navigation",
    sx: [popperContainer, sxPopper]
  }, function (_ref2) {
    var TransitionProps = _ref2.TransitionProps;
    return /*#__PURE__*/React.createElement(Fade, _extends({}, TransitionProps, {
      timeout: 350
    }), /*#__PURE__*/React.createElement(Paper, {
      elevation: 2,
      sx: menuListWrapper
    }, /*#__PURE__*/React.createElement(List, {
      sx: listRoot,
      ref: menuListRef,
      role: "list"
    }, menuItems.map(function (_ref3, index) {
      var value = _ref3.value,
          itemAction = _ref3.itemAction,
          disabled = _ref3.disabled,
          dataProp = _ref3.dataProp;
      var key = "CMWKey:".concat(index);
      return /*#__PURE__*/React.createElement(ListItem, {
        button: true,
        alignItems: "center",
        "aria-label": value,
        onClick: itemAction,
        key: key,
        sx: listItem,
        disabled: disabled,
        onBlur: handleBlur,
        component: "li",
        onKeyDown: function onKeyDown(e) {
          return handleKeyDown(e, index);
        },
        role: "listitem",
        "data-selector": dataProp
      }, /*#__PURE__*/React.createElement(Typography, {
        variant: "body1"
      }, value));
    }))));
  }));
};

var ContextualMenuPopper = /*#__PURE__*/React.memo(ContextualMenuPopperBase);
ContextualMenuPopper.displayName = 'ContextualMenuPopper';
export { ContextualMenuPopper, findFirstFocusable, findLastFocusable, keyDownOpenClose };